import { motion } from "framer-motion";
import type { Persona } from "../OnboardingFlow";

/**
 * Beyond Sure — persona-tailored starter prompts.
 *
 * Lives under the EmptyState greeting. Each card is a tappable prompt
 * that fires the same `onPick(text)` callback as if the user typed it,
 * keeping the path through `handleSend` identical to the typed flow.
 *
 * Customer set:
 *   - Compare health plans for my family
 *   - Is my current policy any good?
 *   - Explain my policy's fine print
 *
 * Agent / Agency / BFSI set:
 *   - Compare plans for a 45-yr-old client
 *   - Audit this client's policy
 *   - What's renewing this month
 *
 * The "explain fine print" / "renewing this month" prompts each map to
 * the audit / renewal branches in pickMockReply so the flow stays
 * coherent end-to-end.
 */

interface Starter {
  label: string;
  prompt: string;
  /** Short hint shown under the label. */
  hint: string;
}

const CUSTOMER_STARTERS: Starter[] = [
  {
    label: "Compare health plans for my family",
    prompt: "Compare health plans for my family",
    hint: "6-insurer side-by-side · my pick + why",
  },
  {
    label: "Is my current policy any good?",
    prompt: "Audit my current policy and tell me what to watch.",
    hint: "Truth-audit · sub-limits, claim ratio, gaps",
  },
  {
    label: "Explain my policy's fine print",
    prompt:
      "Walk me through the fine print of my current policy — what's covered, what isn't.",
    hint: "Plain-language fine-print check",
  },
];

const PARTNER_STARTERS: Starter[] = [
  {
    label: "Compare plans for a 45-yr-old client",
    prompt:
      "Compare the best 6 health plans for a 45-yr-old client with one spouse + one child.",
    hint: "6-insurer side-by-side · ranked by fit",
  },
  {
    label: "Audit this client's policy",
    prompt:
      "Audit this client's existing policy and flag what to discuss in the next call.",
    hint: "Truth-audit · talking points for the call",
  },
  {
    label: "What's renewing this month",
    prompt: "What's renewing this month for me? Sorted by date.",
    hint: "Renewal pipeline · with action recommendation",
  },
];

interface StarterPromptsProps {
  persona: Persona | null | undefined;
  onPick: (prompt: string) => void;
}

export function StarterPrompts({ persona, onPick }: StarterPromptsProps) {
  const starters =
    persona && persona !== "customer" ? PARTNER_STARTERS : CUSTOMER_STARTERS;
  return (
    <div
      data-testid="starter-prompts"
      className="grid grid-cols-1 sm:grid-cols-3 gap-2.5"
    >
      {starters.map((s, i) => (
        <motion.button
          key={s.label}
          type="button"
          onClick={() => onPick(s.prompt)}
          data-testid={`starter-${i}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.05, duration: 0.22 }}
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.98 }}
          className={[
            "flex flex-col items-start gap-1 text-left",
            "rounded-[14px] px-3.5 py-3",
            "bg-[rgb(255_255_255_/_0.7)] backdrop-blur-sm",
            "border border-[var(--color-ink-08)]",
            "hover:border-[var(--color-brand-deep)]",
            "hover:bg-[var(--color-brand-soft)]",
            "shadow-[0_2px_10px_-6px_rgba(20,75,51,0.18)]",
            "transition-colors duration-[var(--duration-fast)]",
          ].join(" ")}
        >
          <span className="text-[13.5px] font-semibold text-[var(--color-ink)] leading-[1.3]">
            {s.label}
          </span>
          <span className="text-[11.5px] text-[var(--color-ink-60)] leading-[1.4]">
            {s.hint}
          </span>
        </motion.button>
      ))}
    </div>
  );
}

export default StarterPrompts;
